import { ChartPie } from "@patternfly/react-charts/victory";
import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Content,
	ContentVariants,
	Flex,
	FlexItem,
	Skeleton,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { GripVerticalIcon } from "@patternfly/react-icons";
import { Table, TableText, Tbody, Td, Tr } from "@patternfly/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { getPartnerTrainingDetails } from "../../../api/hydra";
import GraduationCap from "../../../assets/Icon-Red_Hat-Graduation_cap-A-Red-RGB.png";
import type { IPartnerTraining, IPartnerTrainingDetails } from "../../../constants/apiconstants-hydra";
import type { DragHandleProps } from "../../../constants/CardsStructure";
import { ALL_COURSES_LINK, COURSE_LINK, ENROLLED_PARTNER_COURSES_LINK } from "../../../constants/DashBoardUrls";
import { DashboardCard } from "../shared/DashboardCard";
import "./PartnerTrainingCard.scss";

const UnenrolledPartnersTrainingCardBody = () => (
	<Content>
		<Content component="p">
			Our training offerings help you improve customer experiences, deliver Red Hat related services, and increase your
			sales.
		</Content>
		<Content component="p">Get started with training and earn accreditations today.</Content>
	</Content>
);

const EnrolledPartnersTrainingCardBody = () => {
	const queryClient = useQueryClient();
	const partnerTrainingDetails = queryClient.getQueryData<IPartnerTraining>(["partnerTrainingInfo"]);
	return (
		<div className="enrolled-partner-training">
			<Content>
				<Content component={ContentVariants.h4}>Recent learning paths</Content>
			</Content>
			<Table className="enrolled-partner-training__table" variant="compact">
				<Tbody>
					{partnerTrainingDetails?.items?.map((trainingDetail: IPartnerTrainingDetails) => {
						const completionPercentage = parseInt(trainingDetail.progress, 10);
						return (
							<Tr key={trainingDetail.learningPathId}>
								<Td width={80}>
									<TableText wrapModifier="truncate">
										<a target="_blank" rel="noreferrer" href={COURSE_LINK + trainingDetail.learningPathId}>
											{trainingDetail.learningPathName}
										</a>
									</TableText>
								</Td>
								<Td>
									<Flex
										flexWrap={{ default: "nowrap" }}
										alignItems={{ default: "alignItemsCenter" }}
										className="enrolled-partner-training__table__pie-chart-content"
									>
										<FlexItem
											className="enrolled-partner-training__table__pie-chart-content__percentage-text"
											spacer={{ default: "spacerNone" }}
										>
											<TableText wrapModifier="wrap">{completionPercentage}% Completed</TableText>
										</FlexItem>
										<FlexItem>
											<ChartPie
												data={[
													{
														x: "completed",
														y: completionPercentage,
													},
													{
														x: "Remaining",
														y: 100 - completionPercentage,
													},
												]}
												height={30}
												radius={15}
												width={30}
												ariaTitle={"Pie chart displaying the progress of course completion."}
											/>
										</FlexItem>
									</Flex>
								</Td>
							</Tr>
						);
					})}
				</Tbody>
			</Table>
		</div>
	);
};

const PartnerTrainingCard: FC<{ dragHandleProps?: DragHandleProps | undefined }> = ({ dragHandleProps }) => {
	const { data: partnerTrainingInfo, isLoading } = useQuery({
		queryKey: ["partnerTrainingInfo"],
		queryFn: () => getPartnerTrainingDetails(3),
	});
	const hasPartnerEnrolledInTraining = (partnerTrainingInfo?.items?.length ?? 0) > 0;

	if (isLoading) {
		return (
			<Card isFullHeight className="dashboard-widget">
				<CardHeader>
					<Flex direction={{ default: "row" }} alignItems={{ default: "alignItemsCenter" }}>
						<FlexItem spacer={{ default: "spacerSm" }}>
							<Skeleton height="30px" width="200px" />
						</FlexItem>
						<Skeleton height="30px" width="40px" />
					</Flex>
				</CardHeader>
				<CardBody style={{ minHeight: "12em" }}>
					<Stack hasGutter>
						<StackItem>
							<Skeleton fontSize="md" />
						</StackItem>
						<StackItem>
							<Skeleton fontSize="4xl" />
						</StackItem>
						<StackItem>
							<Skeleton fontSize="4xl" />
						</StackItem>
						<StackItem>
							<Skeleton fontSize="4xl" />
						</StackItem>
					</Stack>
				</CardBody>
				<CardFooter>
					<Flex direction={{ default: "row" }} alignItems={{ default: "alignItemsCenter" }}>
						<FlexItem spacer={{ default: "spacerSm" }}>
							<Skeleton height="30px" width="150px" />
						</FlexItem>
						<Skeleton height="30px" width="150px" />
					</Flex>
				</CardFooter>
			</Card>
		);
	}

	if (!hasPartnerEnrolledInTraining) {
		return (
			<DashboardCard
				icon={GraduationCap}
				title="Partner training"
				cardBody={<UnenrolledPartnersTrainingCardBody />}
				primaryAction={{ label: "Get started", href: ALL_COURSES_LINK, openNewTab: true }}
				footerLinks={[{ label: "View all courses", href: ALL_COURSES_LINK, openNewTab: true }]}
				dragHandleProps={dragHandleProps}
			/>
		);
	}

	return (
		<Card isFullHeight className="dashboard-widget">
			<CardHeader
				{...(dragHandleProps && {
					actions: {
						actions: (
							<Button
								icon={<GripVerticalIcon />}
								variant="plain"
								aria-label="Drag to reorder"
								{...dragHandleProps.attributes}
								{...dragHandleProps.listeners}
							/>
						),
						hasNoOffset: true,
					},
				})}
			>
				<Flex alignItems={{ default: "alignItemsCenter" }} gap={{ default: "gapSm" }}>
					<FlexItem>
						<img src={GraduationCap} alt="" aria-hidden style={{ height: "24px", width: "24px" }} />
					</FlexItem>
					<FlexItem>
						<Content>
							<Content component={ContentVariants.h3} style={{ margin: 0 }}>
								<a href="https://training-lms.redhat.com/sso/saml/auth/rhopen?RelayState=clmsbrowsev2.prMain%253Fin_sessionId%253D%2524XLR8_SESSIONID%2524">
									Partner training
								</a>
							</Content>
						</Content>
					</FlexItem>
				</Flex>
			</CardHeader>
			<CardBody style={{ minHeight: "12em" }}>
				<EnrolledPartnersTrainingCardBody />
			</CardBody>
			<CardFooter>
				<Flex gap={{ default: "gapLg" }}>
					<FlexItem>
						<Button variant="link" component="a" href={ALL_COURSES_LINK} target="_blank" isInline>
							View all courses
						</Button>
					</FlexItem>
					<FlexItem>
						<Button variant="link" component="a" href={ENROLLED_PARTNER_COURSES_LINK} target="_blank" isInline>
							View my training
						</Button>
					</FlexItem>
				</Flex>
			</CardFooter>
		</Card>
	);
};

export default PartnerTrainingCard;
